import {logEventBooking} from 'main/analytics/analytics';
import {LogEventBookingEnum} from 'main/analytics/logEventEnum';
import {
	FeaturesEnum,
	OdontoBookingEnum,
	OperationStateType,
	OperationStateTypesEnum,
} from 'presentation/constants';
import {EntryPointCodeEnum} from 'presentation/constants/entryPointCodeEnum';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {resolveBookingError} from 'presentation/modules/booking/shared/utils/resolveBookingError';
import {useMediaQueries} from 'presentation/pages';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {useState} from 'react';
import {usePostOdontoBookingConfirmMutation} from '../../mutations/usePostOdontoBookingConfirmMutation';
import {usePostOdontoRebookMutation} from '../../mutations/usePostOdontoRebookMutation';
import {HandleConfirmationParams} from './types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useNavigate} from 'react-router-dom';

export function useOdontoConfirmationBookingButton() {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const [bookingState, setBookingState] = useState<OperationStateType | null>(null);
	const {isMobile} = useMediaQueries();
	const {refetch} = useGetHomeQuery();
	const {isEnabled: isHideConfirmationBookingModalEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_HIDE_CONFIRMATION_BOOKING_MODAL,
	);
	const navigate = useNavigate();

	const {bookingNumber, isRebooking} = bookingData;

	const {mutateAsync: postOdontoBookingConfirmMutation} = usePostOdontoBookingConfirmMutation();
	const {mutateAsync: postOdontoRebookMutation} = usePostOdontoRebookMutation();

	const isDone = bookingState === OperationStateTypesEnum.DONE;
	const isSubmitting = bookingState === OperationStateTypesEnum.SUBMITTING;
	const isError = bookingState === OperationStateTypesEnum.ERROR;
	const isUnauthorized = bookingState === OperationStateTypesEnum.UNAUTHORIZED;
	const isUnavailable = bookingState === OperationStateTypesEnum.UNAVAILABLE;

	const hasHourOrAvailabilityError = isUnavailable;
	const isGenericError = isError;

	const handleBookingAction = () => {
		if (isRebooking && bookingNumber) {
			return rebookOdonto(bookingNumber);
		}
		return bookOdonto();
	};

	async function bookOdonto() {
		try {
			setBookingState(OperationStateTypesEnum.SUBMITTING);

			const {consultaMarcada} = await postOdontoBookingConfirmMutation({
				cdEspecialidade: Number(bookingData.specialtyCode),
				cdSubEspecialidade: bookingData.subSpecialtyCode
					? Number(bookingData.subSpecialtyCode)
					: 0,
				cdPrestadorFisico: bookingData.professionalCode,
				cdPrestadorJuridico: bookingData.unitCode,
				dtConsulta: bookingData.dateAt,
				horarioConsulta: bookingData.hourAt,
				cdPontoEntrada: EntryPointCodeEnum.V1,
			});

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_DENTAL_BOOKING_COMPLETE});

			handleConfirmOperation({
				id: consultaMarcada.numero.toString(),
				protocolNumber: consultaMarcada.nuProtocolo.toString(),
			});
		} catch (error) {
			const {state} = resolveBookingError(error);
			setBookingState(state);
		}
	}

	async function rebookOdonto(bookingNumber: string) {
		try {
			setBookingState(OperationStateTypesEnum.SUBMITTING);
			const {reagendamento} = await postOdontoRebookMutation({
				cdEspecialidade: Number(bookingData.specialtyCode),
				cdSubEspecialidade: bookingData.subSpecialtyCode
					? Number(bookingData.subSpecialtyCode)
					: 0,
				cdPrestadorFisico: bookingData.professionalCode,
				cdPrestadorJuridico: bookingData.unitCode,
				dtConsulta: bookingData.dateAt,
				horarioConsulta: bookingData.hourAt,
				cdPontoEntrada: EntryPointCodeEnum.V1,
				nuConsulta: bookingNumber,
			});

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_DENTAL_BOOKING_COMPLETE});

			handleConfirmOperation({
				id: reagendamento.numero.toString(),
				protocolNumber: reagendamento.nuProtocolo.toString(),
			});
		} catch (error) {
			const {state} = resolveBookingError(error);
			setBookingState(state);
		}
	}

	const handleConfirmOperation = async ({id, protocolNumber}: HandleConfirmationParams) => {
		setBookingData(prev => ({
			...prev,
			protocolNumber,
			bookingType: BookingTypeEnum.ODONTO,
			bookingNumber: id,
		}));

		await refetch();

		if (isHideConfirmationBookingModalEnabled) {
			const path = OdontoBookingEnum.ODONTO_BOOKING_CONFIRMATION.replace(':id', id);

			navigate(path);
			setBookingState(null);
		} else {
			setBookingState(OperationStateTypesEnum.DONE);
		}
	};

	return {
		isMobile,
		handleBookingAction,
		isSubmitting,
		isGenericError,
		isUnauthorized,
		isDone,
		setBookingState,
		hasHourOrAvailabilityError,
	};
}
